import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { BadgeWithModal } from "@/components/BadgeWithModal";
import { evolveBadges } from "@/lib/badges";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { getDisplayPlays, formatDisplayPlays } from "@/lib/promoPlays";
import CommentsDrawer from "@/components/CommentsDrawer";
import { getPublicShareUrl } from "@/lib/publicUrl";
import { useShareVisibility } from "@/lib/useShareVisibility";
import { SinglesLockBadge, isSinglesForSale } from "@/components/singles/SinglesLockBadge";

// Play icon
const PlayIcon = ({ className = "w-16 h-16" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// Pause icon
const PauseIcon = ({ className = "w-16 h-16" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

// Heart icon
const HeartIcon = ({ filled, className = "w-8 h-8" }) => (
  <svg className={className} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

// Comment icon
const CommentIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

// Share icon
const ShareIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

// Profile icon
const ProfileIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

// Microphone icon for logo
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

// Badge component - uses BadgeWithModal (clickable, single source of truth)
const Badge = ({ type }) => <BadgeWithModal type={type} size="small" />;

// Feed source badge
const SourceBadge = ({ source }) => {
  const sources = {
    featured: { label: "Featured", icon: "⭐", className: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
    trending: { label: "Trending", icon: "🔥", className: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
    new: { label: "New", icon: "✨", className: "bg-green-500/20 text-green-300 border-green-500/30" },
    random: { label: "Discover", icon: "🎲", className: "bg-purple-500/20 text-purple-300 border-purple-500/30" }
  };
  const src = sources[source];
  if (!src) return null;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${src.className}`}>
      {src.icon} {src.label}
    </span>
  );
};

// Single Track Card Component
const TrackCard = ({ 
  track, 
  isActive, 
  onPlay, 
  onPause, 
  isPlaying,
  onLike,
  isLiked,
  currentUser,
  audioRef,
  onEnded
}) => {
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [listenTime, setListenTime] = useState(0);
  const [playCounted, setPlayCounted] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const listenIntervalRef = useRef(null);
  // Feb 2026 — CEO share-visibility gate. Feed cards are Track surfaces.
  const { canShare } = useShareVisibility();
  const tracksShareOn = canShare("tracks");

  const recordPlay = useCallback(async () => {
    if (playCounted) return;
    try {
      await api.post(`/discover/play/${track.submission_id}?duration_seconds=5`);
      setPlayCounted(true);
    } catch (error) {
      console.error("Failed to record play:", error);
    }
  }, [playCounted, track.submission_id]);

  // Track listen time and count plays
  useEffect(() => {
    if (isActive && isPlaying && !playCounted) {
      listenIntervalRef.current = setInterval(() => {
        setListenTime(prev => {
          const newTime = prev + 1;
          // Count play after 5 seconds
          if (newTime >= 5 && !playCounted) {
            recordPlay();
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (listenIntervalRef.current) {
        clearInterval(listenIntervalRef.current);
      }
    }

    return () => {
      if (listenIntervalRef.current) {
        clearInterval(listenIntervalRef.current);
      }
    };
  }, [isActive, isPlaying, playCounted, recordPlay]);

  // Reset when track changes
  useEffect(() => {
    setListenTime(0);
    setPlayCounted(false);
    setProgress(0);
  }, [track.submission_id]);

  // Update progress and duration
  useEffect(() => {
    if (audioRef?.current && isActive) {
      const audio = audioRef.current;
      
      const updateProgress = () => {
        if (audio && !isNaN(audio.duration) && audio.duration > 0) {
          setProgress((audio.currentTime / audio.duration) * 100 || 0);
          setDuration(audio.duration);
        }
      };
      
      const handleLoadedMetadata = () => {
        if (audio && !isNaN(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
      };
      
      const handleDurationChange = () => {
        if (audio && !isNaN(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
      };
      
      // Try to get duration immediately if already loaded
      if (audio.readyState >= 1 && !isNaN(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
      
      audio.addEventListener('timeupdate', updateProgress);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('durationchange', handleDurationChange);
      audio.addEventListener('canplay', handleDurationChange);
      
      return () => {
        audio.removeEventListener('timeupdate', updateProgress);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('durationchange', handleDurationChange);
        audio.removeEventListener('canplay', handleDurationChange);
      };
    }
  }, [audioRef, isActive]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (audioRef?.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * duration;
    }
  };

  const handleShare = async () => {
    const shareUrl = getPublicShareUrl(`/track/${track.submission_id}`);
    try {
      if (navigator.share) {
        await navigator.share({
          title: track.track_title,
          text: `Check out "${track.track_title}" by ${track.artist_display_name} on RGM Studio`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  return (
    <div 
      className="relative w-full h-full flex flex-col"
      data-testid={`feed-track-${track.submission_id}`}
    >
      {/* Background Cover Art - Tap to play/pause */}
      <div 
        className="absolute inset-0 z-0 cursor-pointer"
        onClick={(e) => {
          // Only toggle play if clicking on background, not on interactive elements
          const target = e.target;
          const isInteractive = target.closest('a') || target.closest('button') || target.closest('[role="button"]');
          if (!isInteractive) {
            isPlaying ? onPause() : onPlay();
          }
        }}
      >
        <img
          src={getCoverUrl(track, 'cover')}
          alt={track.track_title}
          className="w-full h-full object-contain"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=800&fit=crop'; }}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-end p-6 pb-24">
        {/* Source Badge */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <SourceBadge source={track.feed_source} />
          {isSinglesForSale(track) && (
            <SinglesLockBadge
              track={track}
              variant="row"
              testIdSuffix={`feed-${track.submission_id}`}
            />
          )}
        </div>

        {/* Track Info */}
        <div className="mb-4">
          <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg" data-testid="track-title">
            {track.track_title || 'Untitled'}
          </h2>
          <Link to={`/artist/${track.user_id}`} onClick={(e) => e.stopPropagation()} className="inline-block group">
            <div className="flex items-center gap-3">
              {track.artist_photo ? (
                <img
                  src={getProfilePhotoUrl({ profile_photo: track.artist_photo })}
                  alt={track.artist_display_name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold">
                  {(track.artist_display_name || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <span className="text-white font-semibold text-lg group-hover:text-purple-300 transition-colors">
                  {track.artist_display_name}
                </span>
                <div className="flex gap-1 mt-1">
                  {evolveBadges(track.artist_badges)?.slice(0, 2).map(badge => (
                    <Badge key={badge} type={badge} />
                  ))}
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Genre & Stats */}
        <div className="flex items-center gap-4 text-white/70 text-sm mb-6">
          {track.genre && (
            <span className="px-2 py-1 bg-white/10 rounded-full">{track.genre}</span>
          )}
          <span className="flex items-center gap-1">
            <span>▶️</span> {formatDisplayPlays(getDisplayPlays(track))} plays
          </span>
          <span className="flex items-center gap-1">
            <span>❤️</span> {(track.likes_count || 0).toLocaleString()}
          </span>
        </div>

        {/* Progress Bar */}
        <div 
          className="w-full h-1 bg-white/20 rounded-full cursor-pointer mb-2"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-white/50 mb-4">
          <span>{formatTime(audioRef?.current?.currentTime || 0)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Center Play/Pause Button */}
      <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
        <button
          onClick={(e) => {
            e.stopPropagation();
            isPlaying ? onPause() : onPlay();
          }}
          className="pointer-events-auto w-20 h-20 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-all transform hover:scale-110 active:scale-95"
          data-testid="play-pause-btn"
        >
          {isPlaying ? <PauseIcon className="w-10 h-10" /> : <PlayIcon className="w-10 h-10 ml-1" />}
        </button>
      </div>

      {/* Side Actions */}
      <div className="absolute right-4 bottom-32 z-30 flex flex-col items-center gap-6">
        {/* Like */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onLike(track.submission_id);
          }}
          className={`flex flex-col items-center gap-1 transition-transform hover:scale-110 active:scale-95 ${isLiked ? 'text-red-500' : 'text-white'}`}
          data-testid="like-btn"
        >
          <HeartIcon filled={isLiked} className="w-8 h-8 drop-shadow-lg" />
          <span className="text-xs font-semibold drop-shadow">{track.likes_count || 0}</span>
        </button>

        {/* Comment — opens inline drawer (audio keeps playing) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCommentsOpen(true);
          }}
          className="flex flex-col items-center gap-1 text-white hover:text-purple-300 transition-colors"
          data-testid="comment-btn"
        >
          <CommentIcon className="w-8 h-8 drop-shadow-lg" />
          <span className="text-xs font-semibold drop-shadow">Comment</span>
        </button>

        {/* Share */}
        {tracksShareOn && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShare();
            }}
            className="flex flex-col items-center gap-1 text-white hover:text-purple-300 transition-colors"
            data-testid="share-btn"
          >
            <ShareIcon className="w-8 h-8 drop-shadow-lg" />
            <span className="text-xs font-semibold drop-shadow">Share</span>
          </button>
        )}

        {/* Profile */}
        <Link 
          to={`/artist/${track.user_id}`} 
          onClick={(e) => e.stopPropagation()}
          className="flex flex-col items-center gap-1 text-white hover:text-purple-300 transition-colors"
        >
          <ProfileIcon className="w-8 h-8 drop-shadow-lg" />
          <span className="text-xs font-semibold drop-shadow">Profile</span>
        </Link>
      </div>

      {/* Swipe Hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white/50 text-sm flex items-center gap-2 animate-bounce">
        <span>↑ Swipe for more</span>
      </div>

      {/* Inline comments drawer — audio stays mounted in parent */}
      <CommentsDrawer
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        submissionId={track.submission_id}
        currentUser={currentUser}
        trackTitle={track.track_title}
      />
    </div>
  );
};

const DiscoveryFeedPage = () => {
  const [tracks, setTracks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [likedTracks, setLikedTracks] = useState(new Set());
  const [isPlaying, setIsPlaying] = useState(false);
  
  const containerRef = useRef(null);
  const audioRef = useRef(null);
  const seenIds = useRef(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchFeed();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUser(null);
    }
  };

  const fetchFeed = async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const excludeIds = Array.from(seenIds.current).join(',');
      const response = await api.get(`/discover/feed?page=${pageNum}&limit=10&exclude_ids=${excludeIds}`);
      
      const newTracks = response.data.tracks || [];
      
      // Add to seen IDs
      newTracks.forEach(t => seenIds.current.add(t.submission_id));
      
      if (append) {
        setTracks(prev => [...prev, ...newTracks]);
      } else {
        setTracks(newTracks);
      }
      
      setHasMore(response.data.has_more);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch feed:', error);
      toast.error('Failed to load discovery feed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Handle scroll to change tracks
  const handleScroll = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const itemHeight = container.clientHeight;
    const newIndex = Math.round(scrollTop / itemHeight);

    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < tracks.length) {
      setCurrentIndex(newIndex);
      setIsPlaying(false);
      
      // Load more when near end
      if (newIndex >= tracks.length - 3 && hasMore && !loadingMore) {
        fetchFeed(page + 1, true);
      }
    }
  }, [currentIndex, tracks.length, hasMore, loadingMore, page]);

  // Auto-play current track
  useEffect(() => {
    if (tracks.length > 0 && audioRef.current) {
      const currentTrack = tracks[currentIndex];
      // Strict Cloudinary-only: only play if we have a real https URL.
      // Ephemeral /api/public/music paths don't survive redeploy — skip them.
      // Singles Store — locked tracks route through the server-generated
      // preview endpoint (30-45s Cloudinary excerpt). The backend has
      // already redacted `music_url` for these; never fall back to a
      // cached master.
      let audioUrl = '';
      if (currentTrack?.singles_locked === true) {
        const preview = currentTrack.singles_preview_url || '';
        if (preview) {
          audioUrl = preview.startsWith('http')
            ? preview
            : `${window.location.origin}${preview.startsWith('/') ? '' : '/'}${preview}`;
        }
      } else if (currentTrack?.music_url && currentTrack.music_url.startsWith('http')) {
        audioUrl = currentTrack.music_url;
      }

      if (audioUrl) {
        audioRef.current.src = audioUrl;
        audioRef.current.load();
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch((err) => {
          console.log('Auto-play blocked by browser:', err);
          setIsPlaying(false);
        });
      } else {
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
        setIsPlaying(false);
      }
    }
  }, [currentIndex, tracks]);

  const handlePlay = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleEnded = () => {
    // Auto-advance to next track
    if (currentIndex < tracks.length - 1) {
      const container = containerRef.current;
      if (container) {
        container.scrollTo({
          top: (currentIndex + 1) * container.clientHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleLike = async (submissionId) => {
    if (!currentUser) {
      toast.error("Please login to like tracks", {
        action: {
          label: "Login",
          onClick: () => navigate('/login')
        }
      });
      return;
    }

    try {
      // Call the toggle endpoint - server handles both like and unlike
      const response = await api.post(`/track/${submissionId}/like`);
      const { action, like_count, liked } = response.data;
      
      // Update liked state from server response
      if (liked) {
        setLikedTracks(prev => new Set([...prev, submissionId]));
        toast.success("Liked!");
      } else {
        setLikedTracks(prev => {
          const newSet = new Set(prev);
          newSet.delete(submissionId);
          return newSet;
        });
        toast.success("Removed like");
      }
      
      // Update track likes count from server
      setTracks(prev => prev.map(t => 
        t.submission_id === submissionId 
          ? { ...t, likes_count: like_count, likes: liked 
              ? [...(t.likes || []), currentUser.user_id].filter((v, i, a) => a.indexOf(v) === i)
              : (t.likes || []).filter(id => id !== currentUser.user_id) 
            }
          : t
      ));
    } catch (error) {
      console.error('Failed to like track:', error);
      toast.error('Failed to update like');
    }
  };

  // Check which tracks user has liked
  useEffect(() => {
    if (currentUser && tracks.length > 0) {
      const liked = new Set(
        tracks
          .filter(t => t.likes?.includes(currentUser.user_id))
          .map(t => t.submission_id)
      );
      setLikedTracks(liked);
    }
  }, [currentUser, tracks]);

  if (loading) {
    return (
      <div className="h-screen w-full bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-white">Loading Discovery Feed...</p>
        </div>
      </div>
    );
  }

  if (tracks.length === 0) {
    return (
      <div className="h-screen w-full bg-black flex items-center justify-center" data-testid="empty-feed">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🎵</div>
          <h2 className="text-2xl font-bold text-white mb-2">No tracks yet</h2>
          <p className="text-gray-400 mb-6">Be the first to submit your music!</p>
          <Link to="/login">
            <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              Submit Your Music
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-black overflow-hidden" data-testid="discovery-feed-page">
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* Header */}
      <div className="fixed left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent p-4" style={{ top: "var(--rgm-app-banner-h, 0px)" }}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/rgm-logo.png" 
              alt="RGM Studio" 
              className="h-10 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/charts">
              <Button variant="ghost" size="sm" className="text-white hover:text-purple-300">
                🔥 Charts
              </Button>
            </Link>
            <Link to="/explore">
              <Button variant="ghost" size="sm" className="text-white hover:text-purple-300">
                Explore
              </Button>
            </Link>
            {currentUser ? (
              <Link to="/dashboard">
                <Button size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  Login
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Feed */}
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
        onScroll={handleScroll}
        style={{ scrollSnapType: 'y mandatory' }}
      >
        {tracks.map((track, index) => (
          <div
            key={track.submission_id}
            className="h-screen w-full snap-start snap-always"
            style={{ scrollSnapAlign: 'start' }}
          >
            <TrackCard
              track={track}
              isActive={index === currentIndex}
              onPlay={handlePlay}
              onPause={handlePause}
              isPlaying={isPlaying && index === currentIndex}
              onLike={handleLike}
              isLiked={likedTracks.has(track.submission_id)}
              currentUser={currentUser}
              audioRef={audioRef}
              onEnded={handleEnded}
            />
          </div>
        ))}

        {/* Loading More Indicator */}
        {loadingMore && (
          <div className="h-24 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        )}
      </div>

      {/* Track Counter */}
      <div className="fixed bottom-4 left-4 z-50 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm">
        {currentIndex + 1} / {tracks.length}
      </div>
    </div>
  );
};

export default DiscoveryFeedPage;
